import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, FileCode2, Sparkles, Palette, Image as ImageIcon,
  Eye, EyeOff, Hash, Code2, Wand2, ChevronDown, ChevronRight, Layout, Type, Megaphone, Upload,
} from "lucide-react";
import {
  SOCIAL_PLATFORMS, LAYOUTS, FONTS,
  defaultBuilderState, generateSignatureHtml,
} from "@/lib/signatureGenerator";

function Section({ icon: Icon, title, open, onToggle, children }) {
  return (
    <div className="border-b border-white/5 last:border-b-0">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-100"><Icon size={15} className="text-emerald-400" /> {title}</span>
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>
      {open && <div className="pb-4 space-y-3">{children}</div>}
    </div>
  );
}

// Mini diagram representing the layout structure (for the layout picker)
function LayoutPreview({ type, active, brandColor, accentColor }) {
  const bc = active ? brandColor : "#475569";
  const ac = active ? accentColor : "#64748b";
  const muted = "#94a3b8";
  const bg = active ? "rgba(16,185,129,0.06)" : "rgba(255,255,255,0.03)";
  const common = { width: 64, height: 44, viewBox: "0 0 64 44", style: { background: bg, borderRadius: 4, flexShrink: 0 } };
  switch (type) {
    case "side-divider":
      return <svg {...common}><rect x="6" y="8" width="14" height="28" rx="2" fill={bc} /><line x1="22" y1="6" x2="22" y2="38" stroke={ac} strokeWidth="1.4" /><rect x="26" y="10" width="22" height="3" fill={bc} /><rect x="26" y="17" width="32" height="2" fill={muted} /><rect x="26" y="22" width="28" height="2" fill={muted} /><rect x="26" y="27" width="30" height="2" fill={muted} /></svg>;
    case "aurora":
      return <svg {...common}><rect x="5" y="6" width="22" height="5" fill={bc} /><rect x="5" y="13" width="18" height="2.5" fill={ac} /><rect x="40" y="6" width="18" height="11" rx="1.5" fill={bc} opacity="0.85" /><line x1="5" y1="22" x2="58" y2="22" stroke={ac} strokeWidth="1.4" /><rect x="5" y="26" width="6" height="2" fill={ac} /><rect x="13" y="26" width="20" height="2" fill={muted} /><rect x="5" y="31" width="6" height="2" fill={ac} /><rect x="13" y="31" width="16" height="2" fill={muted} /></svg>;
    case "stacked":
      return <svg {...common}><rect x="22" y="6" width="20" height="9" rx="1.5" fill={bc} /><rect x="14" y="18" width="36" height="3" fill={bc} /><rect x="22" y="24" width="20" height="2" fill={muted} /><line x1="28" y1="29" x2="36" y2="29" stroke={ac} strokeWidth="1.4" /><rect x="14" y="33" width="36" height="2" fill={muted} /></svg>;
    case "bold-header":
      return <svg {...common}><rect x="4" y="6" width="56" height="14" rx="2" fill={bc} /><rect x="7" y="10" width="22" height="3" fill="#fff" /><rect x="7" y="15" width="18" height="2" fill="#ffffff" opacity="0.7" /><rect x="48" y="10" width="10" height="6" rx="1" fill="#fff" opacity="0.85" /><rect x="4" y="22" width="56" height="16" rx="2" fill="#fff" opacity="0.08" /><rect x="8" y="26" width="34" height="2" fill={muted} /><rect x="8" y="30" width="28" height="2" fill={muted} /><rect x="8" y="34" width="20" height="2" fill={muted} /></svg>;
    case "card":
      return <svg {...common}><rect x="4" y="6" width="3" height="32" fill={bc} /><rect x="10" y="8" width="22" height="3" fill={bc} /><rect x="10" y="14" width="18" height="2" fill={muted} /><rect x="10" y="20" width="24" height="2" fill={muted} /><rect x="10" y="25" width="28" height="2" fill={muted} /><rect x="10" y="30" width="22" height="2" fill={muted} /><rect x="44" y="10" width="16" height="18" rx="1.5" fill={bc} opacity="0.7" /></svg>;
    case "minimal":
      return <svg {...common}><rect x="6" y="14" width="30" height="3" fill={bc} /><rect x="6" y="22" width="50" height="2" fill={muted} /><rect x="6" y="28" width="22" height="2" fill={muted} /></svg>;
    default:
      return <svg {...common}></svg>;
  }
}

function ColorInput({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-2 w-full">
      <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-9 h-9 rounded border border-white/10 bg-transparent shrink-0" data-testid={`${testId}-color`} />
      <input type="text" value={value} onChange={e => onChange(e.target.value)} className="input flex-1" data-testid={`${testId}-hex`} />
    </div>
  );
}

function Toggle({ checked, onChange, label, testId }) {
  return (
    <label className="flex items-center justify-between gap-2 text-sm py-1.5 cursor-pointer hover:bg-white/[0.02] -mx-1 px-1 rounded" data-testid={testId}>
      <span className="text-slate-300">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`w-9 h-5 rounded-full transition-colors relative ${checked ? "bg-emerald-500" : "bg-white/10"}`}
        aria-pressed={checked}
      >
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${checked ? "left-4" : "left-0.5"}`} />
      </button>
    </label>
  );
}

export default function Designer() {
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState("");
  const [state, setState] = useState(defaultBuilderState());
  const [customHtml, setCustomHtml] = useState("");
  const [mode, setMode] = useState("visual"); // visual | code
  const [preview, setPreview] = useState("");
  const [previewUserId, setPreviewUserId] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const skipDirtyRef = useRef(false);
  const logoFileRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [openSections, setOpenSections] = useState({
    layout: true, brand: true, fields: true, socials: true, banner: false, html: false,
  });
  const toggleSection = (k) => setOpenSections(s => ({ ...s, [k]: !s[k] }));

  // Mark dirty when content changes (but not when we programmatically load a template)
  useEffect(() => {
    if (skipDirtyRef.current) { skipDirtyRef.current = false; return; }
    setDirty(true);
  }, [name, state, customHtml, mode]);

  // Warn before unloading the page if there are unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const loadAll = async (preferId) => {
    const [t, s, u] = await Promise.all([
      api.get("/templates"),
      api.get("/settings"),
      api.get("/users"),
    ]);
    setTemplates(t.data);
    setSettings(s.data);
    setUsers(u.data);
    // Selection priority: explicit preferId > existing selectedId > localStorage > active template > first
    if (t.data.length) {
      const lastViewed = localStorage.getItem("designer_last_template_id");
      const priority = [preferId, selectedId, lastViewed, s.data.activeTemplateId].filter(Boolean);
      const chosen = priority.map(id => t.data.find(x => x.id === id)).find(Boolean) || t.data[0];
      if (chosen && chosen.id !== selectedId) {
        loadTemplate(chosen);
      }
    }
  };
  useEffect(() => { loadAll(); }, []);

  const loadTemplate = (t) => {
    skipDirtyRef.current = true;
    setSelectedId(t.id);
    setName(t.name);
    localStorage.setItem("designer_last_template_id", t.id);
    if (t.builderState) {
      setState({ ...defaultBuilderState(), ...t.builderState });
      setCustomHtml(t.html);
      setMode("visual");
    } else {
      setCustomHtml(t.html);
      setMode("code");
    }
    setDirty(false);
  };

  const html = useMemo(() => {
    return mode === "code" ? customHtml : generateSignatureHtml(state);
  }, [mode, state, customHtml]);

  useEffect(() => {
    if (!html) { setPreview(""); return; }
    const t = setTimeout(() => {
      api.post("/preview", { template_html: html, user_id: previewUserId || undefined })
        .then(r => setPreview(r.data.html)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [html, previewUserId, settings]);

  const newTemplate = () => {
    skipDirtyRef.current = true;
    setSelectedId(null);
    setName("New Signature");
    setState(defaultBuilderState());
    setCustomHtml("");
    setMode("visual");
    setDirty(true); // it's an unsaved new draft
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = { name, html, builderState: mode === "visual" ? state : null };
      let newId = selectedId;
      if (selectedId) {
        await api.put(`/templates/${selectedId}`, payload);
        toast.success("Template updated");
      } else {
        const r = await api.post(`/templates`, payload);
        newId = r.data.id;
        setSelectedId(newId);
        localStorage.setItem("designer_last_template_id", newId);
        toast.success("Template created");
      }
      await loadAll(newId);
      setDirty(false);
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Delete template "${name}"?`)) return;
    await api.delete(`/templates/${selectedId}`);
    localStorage.removeItem("designer_last_template_id");
    toast.success("Deleted");
    newTemplate();
    loadAll();
  };

  const setActive = async () => {
    if (!selectedId) return;
    await api.put("/settings", { ...settings, activeTemplateId: selectedId });
    toast.success("Marked as active template");
    loadAll();
  };

  const update = (patch) => setState(s => ({ ...s, ...patch }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const data = new FormData();
      data.append("image", file);
      const r = await api.post("/uploads/logo", data, { headers: { "Content-Type": "multipart/form-data" } });
      update({ logoUrl: r.data.url });
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLogoUploading(false);
      if (logoFileRef.current) logoFileRef.current.value = "";
    }
  };
  const updateShow = (k, v) => setState(s => ({ ...s, show: { ...s.show, [k]: v } }));
  const updateLabel = (k, v) => setState(s => ({ ...s, labels: { ...s.labels, [k]: v } }));
  const addSocial = () => setState(s => ({ ...s, socials: [...(s.socials || []), { platform: "instagram", url: "" }] }));
  const updateSocial = (i, patch) => setState(s => ({ ...s, socials: s.socials.map((x, idx) => idx === i ? { ...x, ...patch } : x) }));
  const removeSocial = (i) => setState(s => ({ ...s, socials: s.socials.filter((_, idx) => idx !== i) }));

  const isActive = selectedId && settings?.activeTemplateId === selectedId;

  return (
    <div className="p-8 max-w-[1500px]">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest mb-1">
            <Wand2 size={14} /> Builder
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Signature Designer</h1>
          <p className="text-slate-400 mt-1 text-sm max-w-2xl">
            Visual builder with layouts, brand colors, social icons. Personalises per user with M365 profile data on deploy.
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-white/[0.04] border border-white/10 rounded-md p-0.5">
            <button onClick={() => setMode("visual")} className={`px-3 py-1.5 text-xs rounded transition-colors ${mode === "visual" ? "bg-emerald-500/20 text-emerald-300" : "text-slate-400"}`} data-testid="mode-visual"><Wand2 size={12} className="inline mr-1" /> Visual</button>
            <button onClick={() => setMode("code")} className={`px-3 py-1.5 text-xs rounded transition-colors ${mode === "code" ? "bg-emerald-500/20 text-emerald-300" : "text-slate-400"}`} data-testid="mode-code"><Code2 size={12} className="inline mr-1" /> HTML</button>
          </div>
          <button onClick={newTemplate} className="btn" data-testid="new-template"><Plus size={14} /> New</button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {templates.map(t => (
          <button
            key={t.id}
            onClick={() => loadTemplate(t)}
            data-testid={`template-tab-${t.id}`}
            className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
              selectedId === t.id
                ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                : "bg-white/[0.03] text-slate-300 border-white/10 hover:bg-white/[0.06]"
            }`}
          >
            {t.name} {settings?.activeTemplateId === t.id && <span className="ml-1 text-[10px] text-emerald-400">● active</span>}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
        {/* LEFT: builder */}
        <div className="glass p-4 max-h-[80vh] overflow-y-auto">
          <div className="mb-3">
            <div className="label">Template name</div>
            <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="e.g. Wilson Standard" data-testid="template-name" />
          </div>

          {mode === "visual" ? (
            <>
              <Section icon={Layout} title="Layout" open={openSections.layout} onToggle={() => toggleSection("layout")}>
                <div className="grid grid-cols-1 gap-2">
                  {LAYOUTS.map(l => (
                    <button
                      key={l.key}
                      type="button"
                      onClick={() => update({ layout: l.key })}
                      data-testid={`layout-${l.key}`}
                      className={`text-left p-2.5 rounded border transition-colors flex items-center gap-3 ${state.layout === l.key ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"}`}
                    >
                      <LayoutPreview type={l.key} active={state.layout === l.key} brandColor={state.brandColor} accentColor={state.accentColor} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-100">{l.label}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">{l.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </Section>

              <Section icon={Palette} title="Brand & typography" open={openSections.brand} onToggle={() => toggleSection("brand")}>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="label">Primary</div>
                    <ColorInput value={state.brandColor} onChange={v => update({ brandColor: v })} testId="brand" />
                  </div>
                  <div>
                    <div className="label">Accent</div>
                    <ColorInput value={state.accentColor} onChange={v => update({ accentColor: v })} testId="accent" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><div className="label">Text</div><ColorInput value={state.textColor} onChange={v => update({ textColor: v })} testId="text" /></div>
                  <div><div className="label">Muted</div><ColorInput value={state.mutedColor} onChange={v => update({ mutedColor: v })} testId="muted" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="label">Font</div>
                    <select className="select" value={state.fontFamily} onChange={e => update({ fontFamily: e.target.value })} data-testid="font-family">
                      {FONTS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <div className="label">Size (px)</div>
                    <input type="number" min={10} max={18} className="input" value={state.fontSize} onChange={e => update({ fontSize: parseInt(e.target.value || 13) })} data-testid="font-size" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="label">Logo</div>
                    <div className="flex gap-1">
                      <input className="input flex-1" value={state.logoUrl} onChange={e => update({ logoUrl: e.target.value })} placeholder="URL or {{logoUrl}}" data-testid="logo-url" />
                      <input ref={logoFileRef} type="file" accept="image/*" className="hidden" onChange={e => uploadLogo(e.target.files?.[0])} data-testid="logo-upload-input" />
                      <button onClick={() => logoFileRef.current?.click()} disabled={logoUploading} className="btn !px-2" title="Upload logo" data-testid="logo-upload-btn">
                        <Upload size={13} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="label">Logo width (px)</div>
                    <input type="number" min={40} max={200} className="input" value={state.logoWidth} onChange={e => update({ logoWidth: parseInt(e.target.value || 90) })} data-testid="logo-width" />
                  </div>
                </div>
              </Section>

              <Section icon={Type} title="Fields shown" open={openSections.fields} onToggle={() => toggleSection("fields")}>
                <div className="grid grid-cols-2 gap-x-3">
                  <Toggle checked={state.show.jobTitle} onChange={v => updateShow("jobTitle", v)} label="Job title" testId="toggle-jobTitle" />
                  <Toggle checked={state.show.department} onChange={v => updateShow("department", v)} label="Department" testId="toggle-department" />
                  <Toggle checked={state.show.company} onChange={v => updateShow("company", v)} label="Company name" testId="toggle-company" />
                  <Toggle checked={state.show.address} onChange={v => updateShow("address", v)} label="Address" testId="toggle-address" />
                  <Toggle checked={state.show.mobile} onChange={v => updateShow("mobile", v)} label="Mobile" testId="toggle-mobile" />
                  <Toggle checked={state.show.businessPhone} onChange={v => updateShow("businessPhone", v)} label="Office phone" testId="toggle-businessPhone" />
                  <Toggle checked={state.show.email} onChange={v => updateShow("email", v)} label="Email" testId="toggle-email" />
                  <Toggle checked={state.show.website} onChange={v => updateShow("website", v)} label="Website" testId="toggle-website" />
                  <Toggle checked={state.show.disclaimer} onChange={v => updateShow("disclaimer", v)} label="Disclaimer" testId="toggle-disclaimer" />
                </div>
                <div className="text-[10px] text-slate-500 mt-2">Field labels (single char):</div>
                <div className="grid grid-cols-4 gap-1.5">
                  {["mobile", "businessPhone", "email", "website"].map(k => (
                    <input key={k} className="input text-center !py-1.5 !px-1" maxLength={3} value={state.labels[k]} onChange={e => updateLabel(k, e.target.value)} title={k} data-testid={`label-${k}`} />
                  ))}
                </div>
              </Section>

              <Section icon={Hash} title="Social icons" open={openSections.socials} onToggle={() => toggleSection("socials")}>
                <Toggle checked={state.show.socials} onChange={v => updateShow("socials", v)} label="Show social row" testId="toggle-socials" />
                {state.show.socials && (
                  <>
                    <div className="space-y-1.5">
                      {(state.socials || []).map((s, i) => (
                        <div key={i} className="flex gap-1.5 items-center" data-testid={`social-row-${i}`}>
                          <select value={s.platform} onChange={e => updateSocial(i, { platform: e.target.value })} className="select max-w-[110px] !py-1.5" data-testid={`social-platform-${i}`}>
                            {SOCIAL_PLATFORMS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                          </select>
                          <input value={s.url} onChange={e => updateSocial(i, { url: e.target.value })} className="input flex-1 !py-1.5" placeholder="https://..." data-testid={`social-url-${i}`} />
                          <button onClick={() => removeSocial(i)} className="btn btn-danger !px-2 !py-1.5" title="Remove" data-testid={`social-remove-${i}`}><Trash2 size={12} /></button>
                        </div>
                      ))}
                    </div>
                    <button onClick={addSocial} className="btn w-full justify-center mt-2" data-testid="add-social"><Plus size={13} /> Add icon</button>
                    <div className="text-[10px] text-slate-500">Icons are auto-coloured to your brand colour. Click 'Save' then 'Preview' to see them rendered.</div>
                  </>
                )}
              </Section>

              <Section icon={Megaphone} title="Banner / promo (optional)" open={openSections.banner} onToggle={() => toggleSection("banner")}>
                <Toggle checked={state.show.banner} onChange={v => updateShow("banner", v)} label="Show banner image" testId="toggle-banner" />
                {state.show.banner && (
                  <>
                    <div>
                      <div className="label">Banner image URL (public HTTPS)</div>
                      <input className="input" value={state.bannerUrl} onChange={e => update({ bannerUrl: e.target.value })} placeholder="https://..." data-testid="banner-url" />
                    </div>
                    <div>
                      <div className="label">Click-through URL (optional)</div>
                      <input className="input" value={state.bannerHref} onChange={e => update({ bannerHref: e.target.value })} placeholder="https://yourcompany.com/promo" data-testid="banner-href" />
                    </div>
                    <div className="text-[10px] text-slate-500">Banner appears below the signature. Great for seasonal campaigns, awards, certifications.</div>
                  </>
                )}
              </Section>

              <Section icon={FileCode2} title="Disclaimer text" open={openSections.html} onToggle={() => toggleSection("html")}>
                <textarea className="textarea" rows={3} value={state.disclaimer} onChange={e => update({ disclaimer: e.target.value })} placeholder="Confidentiality disclaimer..." data-testid="disclaimer-text" />
                <div className="text-[10px] text-slate-500">Use {`{{tagline}}`} to pull from Brand Settings. Or type custom text here.</div>
              </Section>
            </>
          ) : (
            <div>
              <div className="label">HTML (advanced)</div>
              <textarea className="textarea code" rows={26} value={customHtml} onChange={e => setCustomHtml(e.target.value)} spellCheck={false} data-testid="template-html" />
              <div className="text-[11px] text-slate-500 mt-2">Use tokens like {`{{displayName}}`}, {`{{jobTitle}}`}, {`{{mail}}`}, {`{{logoUrl}}`}, etc. Switching to Visual mode regenerates HTML from builder state.</div>
            </div>
          )}

          <div className="flex gap-2 mt-4 pt-4 border-t border-white/5 sticky bottom-0 bg-[#0a0e1c]/95 backdrop-blur items-center">
            <button onClick={save} disabled={saving} className={`btn btn-primary ${dirty ? "ring-2 ring-emerald-500/50 ring-offset-2 ring-offset-[#0a0e1c]" : ""}`} data-testid="save-template">
              <Save size={14} /> {saving ? "Saving…" : selectedId ? "Update" : "Save"}
              {dirty && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-[#0a0e1c]/60" />}
            </button>
            {dirty && (
              <span className="text-[11px] text-amber-300/90" data-testid="unsaved-indicator">● Unsaved changes</span>
            )}
            {selectedId && !isActive && (
              <button onClick={setActive} className="btn" data-testid="set-active">Set as active</button>
            )}
            {selectedId && (
              <button onClick={remove} className="btn btn-danger ml-auto" data-testid="delete-template">
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>
        </div>

        {/* RIGHT: preview */}
        <div className="glass p-4 flex flex-col">
          <div className="mb-3 flex items-center justify-between">
            <div className="label !mb-0">Live Preview</div>
            <select value={previewUserId} onChange={e => setPreviewUserId(e.target.value)} className="select max-w-[260px]" data-testid="preview-user-select">
              <option value="">— Demo data —</option>
              {users.map(u => { const label = `${u.displayName} (${u.mail || u.userPrincipalName})`; return <option key={u.id} value={u.id}>{label}</option>; })}
            </select>
          </div>
          <div className="preview-shell flex-1 min-h-[280px]" data-testid="designer-preview" dangerouslySetInnerHTML={{ __html: preview || "<div style='color:#9ca3af; font-size:12px;'>Preview rendering…</div>" }} />
          <div className="text-[11px] text-slate-500 mt-2">
            Looks how Outlook will render it. Use real users in the dropdown to verify personalization. Tip: social icons are fetched at email render time from a public icon CDN — they'll appear in clients (icon8.com).
          </div>
        </div>
      </div>
    </div>
  );
}
